import React from "react"
import type { AiSubject } from "../../Session/plan-lesson/ai-lesson-planning/aiLessonPlanningTypes"
import {
  SUBJECT_META,
  activityScoreColor,
  shortDate,
  sinceDays,
} from "../learnerReportModel"
import type { PathItem } from "../learnerReportModel"
import { ChevronRight, EmptyNote, SubjectChip } from "../reportUi"
import type { ReportView } from "./reportView"

const SUMMARY_BOXES = [
  { band: "Passed", bg: "#E6F5EC", fg: "#1E6E40", bar: "#2E9D5E" },
  { band: "In progress", bg: "#FFF2DE", fg: "#8A5300", bar: "#E0A040" },
  { band: "Weak", bg: "#FDE6E9", fg: "#A8243A", bar: "#D7354B" },
  { band: "Not started", bg: "#EEF0F5", fg: "#4A5163", bar: "#C3C8D4" },
] as const

const OverviewTab: React.FC<{ view: ReportView }> = ({ view }) => {
  const monthAgo = sinceDays(view.now, 30)
  const recentGroups = view.groups.filter(g => g.last >= monthAgo)

  const topPicks: Array<{ rank: number; item: PathItem; subject: AiSubject }> = []
  for (let i = 0; topPicks.length < 3 && i < 3; i++) {
    view.subjects.forEach(s => {
      const item = view.recs[s].progress[i]
      if (item && topPicks.length < 3) topPicks.push({ rank: i + 1, item, subject: s })
    })
  }
  const totalRecs = view.subjects.reduce((t, s) => t + view.recs[s].total, 0)

  return (
    <>
      <div className="sr-grid-2">
        {view.subjects.map(subject => {
          const sk = view.skills[subject]
          const counts = {
            Passed: sk.counts.Passed,
            "In progress": sk.counts["In progress"] + sk.counts.Good,
            Weak: sk.counts.Weak,
            "Not started": sk.counts["Not started"],
          }
          const total = sk.total || 1
          return (
            <section key={subject} aria-label={`${SUBJECT_META[subject].label} summary`} className="sr-card sr-section">
              <div className="sr-head-baseline">
                <h2 className="sr-h2">{SUBJECT_META[subject].label}</h2>
                <span className="sr-muted">
                  {sk.currentYear !== null ? `Year ${sk.currentYear} · ` : ""}
                  {sk.passed} of {sk.total} skills passed
                </span>
              </div>
              <div className="sr-summary-boxes">
                {SUMMARY_BOXES.map(b => (
                  <div key={b.band} className="sr-summary-box" style={{ background: b.bg }}>
                    <span className="sr-summary-n">{counts[b.band]}</span>
                    <span className="sr-summary-label" style={{ color: b.fg }}>{b.band}</span>
                  </div>
                ))}
              </div>
              <div className="sr-stack-bar" role="img" aria-label={SUMMARY_BOXES.map(b => `${counts[b.band]} ${b.band.toLowerCase()}`).join(", ")}>
                {SUMMARY_BOXES.map(b =>
                  counts[b.band] ? (
                    <div key={b.band} style={{ width: `${(counts[b.band] / total) * 100}%`, background: b.bar }} />
                  ) : null
                )}
              </div>
              <button type="button" className="sr-link" onClick={() => view.goTab(subject === "math" ? "maths" : "english")}>
                View {subject === "math" ? "maths" : "English"} skills →
              </button>
            </section>
          )
        })}
      </div>

      <div className="sr-flex-row">
        <section aria-label="Needs attention" className="sr-card sr-section" style={{ flex: "1 1 380px", minWidth: 0, gap: 14 }}>
          <h2 className="sr-h2">Needs attention</h2>
          {view.subjects.map(subject => {
            const weak = recentGroups
              .filter(g => g.subject === subject && g.best !== null && g.best < 40)
              .sort((a, b) => (a.best as number) - (b.best as number))
              .slice(0, 3)
            return (
              <div key={subject} className="sr-stack-8">
                <span className="sr-caps">{SUBJECT_META[subject].label.toUpperCase()}</span>
                {weak.length === 0 ? (
                  <span className="sr-text-sub">No weak skills in the last month.</span>
                ) : (
                  weak.map(g => (
                    <div key={g.key} className="sr-weak-row">
                      <span className="sr-dot" style={{ background: "#D7354B" }} />
                      <span className="sr-weak-title">{g.title}</span>
                      <span className="sr-mono-score" style={{ color: "#B42335" }}>{(g.best as number).toFixed(1)}</span>
                    </div>
                  ))
                )}
              </div>
            )
          })}
          <button type="button" className="sr-link" onClick={() => view.goTab("history")}>
            See attempts in Learning History →
          </button>
        </section>

        <section aria-label="Learning path" className="sr-card sr-section" style={{ flex: "2 1 560px", minWidth: 0 }}>
          <div className="sr-head-baseline">
            <h2 className="sr-h2">Learning path</h2>
            <button type="button" className="sr-link" onClick={() => view.goTab("roadmap")}>
              Open roadmap →
            </button>
          </div>
          <div className="sr-path-box">
            {view.subjects.map(subject => {
              const path = view.recs[subject].progress.slice(0, 3)
              return (
                <div key={subject} className="sr-path-row">
                  <span className="sr-path-subject">{SUBJECT_META[subject].label}</span>
                  {path.length === 0 ? (
                    <span className="sr-text-sub">No recommendations yet.</span>
                  ) : (
                    path.map((p, i) => (
                      <React.Fragment key={p.sourceId}>
                        {i > 0 ? <ChevronRight size={16} /> : null}
                        <span className={i === 0 ? "sr-path-step sr-path-step-now" : "sr-path-step"} title={p.title}>
                          <b>{p.when}</b> {p.title}
                        </span>
                      </React.Fragment>
                    ))
                  )}
                </div>
              )
            })}
          </div>
        </section>
      </div>

      <section aria-label="Top picks" className="sr-card sr-section">
        <div className="sr-head-baseline">
          <div className="sr-stack-4">
            <h2 className="sr-h2">Recommended next</h2>
            <span className="sr-muted">{totalRecs} recommendations for the next session · top picks</span>
          </div>
          <button type="button" className="sr-link" onClick={view.planLesson}>
            Plan in AI Lesson Planning →
          </button>
        </div>
        {topPicks.length === 0 ? (
          <EmptyNote>No recommendations returned for this student yet.</EmptyNote>
        ) : (
          <div className="sr-pick-grid">
            {topPicks.map(({ rank, item, subject }) => (
              <div key={`${subject}:${item.sourceId}`} className="sr-pick">
                <div className="sr-pick-tags">
                  <span className="sr-pick-rank">#{rank}</span>
                  <span className="sr-subj-chip sr-subj-chip-sm" style={{ background: "#E4EAFB", color: "#2E4AA6" }}>{item.basket}</span>
                  <SubjectChip subject={subject} small />
                </div>
                <span className="sr-pick-title">{item.title}</span>
                <span className="sr-muted">{item.meta}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section aria-label="Recent activity" className="sr-card sr-section" style={{ gap: 14 }}>
        <div className="sr-head-baseline">
          <h2 className="sr-h2">Recent activity</h2>
          <button type="button" className="sr-link" onClick={() => view.goTab("history")}>
            Full learning history →
          </button>
        </div>
        {view.groups.length === 0 ? (
          <EmptyNote>No scored activities yet.</EmptyNote>
        ) : (
          <div className="sr-recent-list">
            {view.groups.slice(0, 4).map(g => (
              <div key={g.key} className="sr-recent-row">
                <span className="sr-recent-date">{shortDate(g.last)}</span>
                <SubjectChip subject={g.subject} small />
                <span className="sr-recent-title">{g.title}</span>
                <span className="sr-mono-score" style={{ color: activityScoreColor(g.best) }}>
                  {g.best === null ? "–" : g.best.toFixed(1)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
      <p className="sr-footnote">
        Skill scores use the best score on each activity at the student's current year.{" "}
        Needs attention lists activities from the last month scoring under 40.
      </p>
    </>
  )
}

export default OverviewTab
